/** Lightweight HTML helpers for paste-with-format fields (quotation notes / T&C). */

import { estimateWrappedLineCount, splitDescriptionChunks } from '@/print-engine/description-chunks';

const ALLOWED_TAGS = new Set([
  'P',
  'BR',
  'DIV',
  'SPAN',
  'STRONG',
  'B',
  'EM',
  'I',
  'U',
  'UL',
  'OL',
  'LI',
  'H1',
  'H2',
  'H3',
  'H4',
  'TABLE',
  'THEAD',
  'TBODY',
  'TR',
  'TH',
  'TD',
]);

const ALLOWED_STYLES = /^(color|background-color|font-weight|font-style|text-decoration|text-align)$/i;

export function looksLikeHtml(value: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(String(value || ''));
}

/** Strip tags for pagination height estimates / plain exports. */
export function richTextToPlain(value: string): string {
  const raw = String(value || '');
  if (!raw.trim()) return '';
  if (!looksLikeHtml(raw)) return raw;

  // Word/HTML editors insert soft line-wraps inside sentences (`Scope of\nWork`).
  // Those must become spaces for measurement — only real block boundaries stay as newlines.
  const BLOCK = '\0';
  return raw
    .replace(/<br\s*\/?>/gi, BLOCK)
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, BLOCK)
    .replace(/<\/(ul|ol|table)>/gi, BLOCK)
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    // Collapse soft-wrap / indentation whitespace (including newlines inside spans).
    .replace(/[\t\f\v\r \n]+/g, ' ')
    .split(BLOCK)
    .map((part) => part.trim())
    .filter(Boolean)
    .join('\n');
}

/** Single-line print value (commercial terms): tags stripped, entities decoded even without tags. */
export function richTextToPlainLine(value: string): string {
  return richTextToPlain(String(value || ''))
    .replace(/&nbsp;/gi, ' ')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Collapse blank / soft-wrapped lines so Word-paste HTML does not inflate height. */
export function estimateDensePrintLines(text: string, charsPerLine: number): number {
  const collapsed = String(text || '')
    .replace(/\r\n/g, '\n')
    // Soft-wrapped plain lines (no bullet/number start) → single visual line.
    .replace(/([^\n])\n(?![ \t]*(?:[-*•]|\d+[.)]\s|$))/g, '$1 ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
  if (!collapsed) return 0;
  return estimateWrappedLineCount(collapsed, Math.max(8, charsPerLine));
}

function sanitizeStyle(style: string): string {
  return style
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const idx = part.indexOf(':');
      if (idx < 0) return '';
      const prop = part.slice(0, idx).trim();
      const val = part.slice(idx + 1).trim();
      if (!ALLOWED_STYLES.test(prop)) return '';
      if (/expression|url\s*\(|javascript:/i.test(val)) return '';
      return `${prop}: ${val}`;
    })
    .filter(Boolean)
    .join('; ');
}

const KEPT_ATTRIBUTES = new Set(['style', 'colspan', 'rowspan']);

const PRUNABLE_WHEN_EMPTY = new Set([
  'P',
  'DIV',
  'SPAN',
  'STRONG',
  'B',
  'EM',
  'I',
  'U',
  'H1',
  'H2',
  'H3',
  'H4',
  'LI',
  'UL',
  'OL',
]);

const HEADING_TAGS = new Set(['H1', 'H2', 'H3', 'H4']);

export type SanitizeRichTextOptions = {
  /** Remove blocks with no visible text (chat/Word wrappers, `<p></p>`). Not for live editing. */
  dropEmptyBlocks?: boolean;
  /** Render H1–H4 as bold paragraphs so pasted headings print at body size. */
  flattenHeadings?: boolean;
};

const PRINT_SANITIZE: SanitizeRichTextOptions = { dropEmptyBlocks: true, flattenHeadings: true };

function pruneEmptyBlocks(root: DocumentFragment) {
  const elements = Array.from(root.querySelectorAll('*')).reverse();
  for (const el of elements) {
    if (!PRUNABLE_WHEN_EMPTY.has(el.tagName.toUpperCase())) continue;
    if ((el.textContent || '').replace(/\u00a0/g, ' ').trim()) continue;
    el.remove();
  }
}

function flattenHeadings(root: DocumentFragment) {
  for (const el of Array.from(root.querySelectorAll('h1,h2,h3,h4'))) {
    if (!HEADING_TAGS.has(el.tagName.toUpperCase())) continue;
    const p = document.createElement('p');
    const strong = document.createElement('strong');
    while (el.firstChild) strong.appendChild(el.firstChild);
    p.appendChild(strong);
    const style = el.getAttribute('style');
    if (style) p.setAttribute('style', style);
    el.replaceWith(p);
  }
}

/** Allow a safe subset of HTML from Word / browser paste. */
export function sanitizeRichTextHtml(html: string, opts: SanitizeRichTextOptions = {}): string {
  const raw = String(html || '').trim();
  if (!raw) return '';
  if (typeof document === 'undefined') {
    return raw.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');
  }

  const template = document.createElement('template');
  template.innerHTML = raw
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '');

  const walk = (node: Node) => {
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as HTMLElement;
        const tag = el.tagName.toUpperCase();
        if (tag === 'META' || tag === 'LINK' || tag === 'XML' || tag === 'O:P') {
          el.remove();
          continue;
        }
        if (!ALLOWED_TAGS.has(tag)) {
          walk(el);
          const parent = el.parentNode;
          if (parent) {
            while (el.firstChild) parent.insertBefore(el.firstChild, el);
            parent.removeChild(el);
          }
          continue;
        }
        for (const attr of Array.from(el.attributes)) {
          const name = attr.name.toLowerCase();
          if (name === 'style') {
            const cleaned = sanitizeStyle(attr.value);
            if (cleaned) el.setAttribute('style', cleaned);
            else el.removeAttribute('style');
            continue;
          }
          if (!KEPT_ATTRIBUTES.has(name)) {
            el.removeAttribute(attr.name);
          }
        }
        walk(el);
      } else if (child.nodeType === Node.COMMENT_NODE) {
        child.parentNode?.removeChild(child);
      }
    }
  };

  walk(template.content);
  if (opts.flattenHeadings) flattenHeadings(template.content);
  if (opts.dropEmptyBlocks) pruneEmptyBlocks(template.content);
  return template.innerHTML.trim();
}

export function escapePlainTextAsHtml(text: string): string {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br/>');
}

/** Normalize stored value for print: sanitized HTML, or escaped plain with line breaks. */
export function richTextToPrintHtml(value: string): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (looksLikeHtml(raw)) return sanitizeRichTextHtml(raw, PRINT_SANITIZE);
  return escapePlainTextAsHtml(raw);
}

function isHeadingLikeBlock(block: string): boolean {
  const plain = richTextToPlain(block).trim();
  if (!plain || plain.length > 140) return false;
  const lines = plain.split('\n').map((l) => l.trim()).filter(Boolean);
  // Multi-line blocks are section bodies, not orphan headings.
  if (lines.length !== 1) return false;
  if (/^\d+[.)]\s+\S/.test(plain)) return true;
  if (/^(inclusion|exclusion|notes?|terms?|conditions?)\b/i.test(plain)) return true;
  return plain.length <= 80 && !/[.!?]$/.test(plain);
}

/** Split into paragraph / list blocks without breaking every numbered clause alone. */
function splitRichTextBlocks(value: string): string[] {
  const raw = String(value || '').trim();
  if (!raw) return [];
  if (looksLikeHtml(raw)) {
    const sanitized = sanitizeRichTextHtml(raw, PRINT_SANITIZE);
    const blocks = sanitized
      .split(/(?=<p\b|<div\b|<h[1-6]\b|<ul\b|<ol\b|<table\b)/i)
      .map((b) => b.trim())
      .filter((b) => richTextToPlain(b).trim());
    return blocks.length ? blocks : [sanitized];
  }
  // Plain text: blank-line paragraphs only (do NOT use item-description numbered splitter).
  const paragraphs = raw
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (paragraphs.length) return paragraphs;
  return [raw];
}

/**
 * Group heading + following bullets into one section so T&C clauses stay together.
 */
function groupBlocksIntoClauses(blocks: string[], html: boolean): string[] {
  if (!blocks.length) return [];
  const clauses: string[] = [];
  let cur: string[] = [];
  const join = () => (html ? cur.join('') : cur.join('\n\n'));

  for (const block of blocks) {
    if (isHeadingLikeBlock(block) && cur.length > 0) {
      clauses.push(join());
      cur = [block];
    } else {
      cur.push(block);
    }
  }
  if (cur.length) clauses.push(join());
  return clauses;
}

/**
 * Split plain text on numbered clause headings (`1. Title`) so planner can pack
 * clause-sized keep-together units instead of pre-sliced half pages.
 */
function splitPlainNumberedClauses(text: string): string[] {
  const lines = String(text || '')
    .replace(/\r\n/g, '\n')
    .split('\n');
  const units: string[] = [];
  let cur: string[] = [];
  for (const line of lines) {
    const trimmed = line.trim();
    const startsNumbered = /^\d+[.)]\s+\S/.test(trimmed);
    if (startsNumbered && cur.some((l) => l.trim())) {
      units.push(cur.join('\n').trim());
      cur = [line];
    } else {
      cur.push(line);
    }
  }
  if (cur.some((l) => l.trim())) units.push(cur.join('\n').trim());
  return units;
}

/**
 * Clause-sized print units for Terms & Conditions.
 * The page planner packs these into available page height (do not pre-pack into
 * page-sized blobs — that caused half-empty T&C pages when estimates were high).
 */
export function buildRichTextClauseChunks(value: string, opts?: { charsPerLine?: number }): string[] {
  const raw = String(value || '').trim();
  if (!raw) return [];
  const cpl = opts?.charsPerLine ?? 110;
  const html = looksLikeHtml(raw);
  // Soft cap for a single oversized clause (~almost one continuation page).
  const maxClauseLines = 48;

  let units: string[];
  if (html) {
    const blocks = splitRichTextBlocks(raw);
    units = groupBlocksIntoClauses(blocks, true);
    // If HTML did not expose numbered headings (one big block), fall back to plain split.
    if (units.length <= 1) {
      const plainUnits = splitPlainNumberedClauses(richTextToPlain(raw));
      if (plainUnits.length > 1) {
        units = plainUnits.map((u) => escapePlainTextAsHtml(u));
      }
    }
  } else {
    const byBlank = splitRichTextBlocks(raw);
    const numbered = splitPlainNumberedClauses(raw);
    // Prefer numbered clause split when it yields more natural T&C sections.
    units = numbered.length > byBlank.length ? numbered : byBlank;
  }

  const chunks: string[] = [];
  for (const unit of units) {
    const unitLines = Math.max(1, estimateDensePrintLines(richTextToPlain(unit), cpl));
    if (unitLines <= maxClauseLines) {
      chunks.push(unit);
      continue;
    }
    const plainLines = richTextToPlain(unit)
      .split('\n')
      .map((l) => l.trimEnd())
      .filter((l) => l.trim());
    let slice: string[] = [];
    let sliceLines = 0;
    for (const pl of plainLines) {
      const plLines = Math.max(1, estimateDensePrintLines(pl, cpl));
      if (sliceLines > 0 && sliceLines + plLines > maxClauseLines) {
        chunks.push(html ? escapePlainTextAsHtml(slice.join('\n')) : slice.join('\n'));
        slice = [];
        sliceLines = 0;
      }
      slice.push(pl);
      sliceLines += plLines;
    }
    if (slice.length) {
      chunks.push(html ? escapePlainTextAsHtml(slice.join('\n')) : slice.join('\n'));
    }
  }

  return chunks.filter((c) => richTextToPlain(c).trim());
}

export type RichTextChunkOptions = {
  /** Soft max wrapped lines per chunk (continuation letter page ≈ 42–48 dense lines). */
  maxLines?: number;
  charsPerLine?: number;
};

/**
 * Pack rich/plain text into page-sized print chunks (notes / special instructions).
 */
export function buildRichTextPrintChunks(value: string, opts?: RichTextChunkOptions): string[] {
  const raw = String(value || '').trim();
  if (!raw) return [];
  const maxLines = opts?.maxLines ?? 44;
  const cpl = opts?.charsPerLine ?? 92;
  const html = looksLikeHtml(raw);
  const blocks = splitRichTextBlocks(raw);
  if (!blocks.length) return [];

  const units = groupBlocksIntoClauses(blocks, html);

  const totalLines = estimateDensePrintLines(richTextToPlain(raw), cpl);
  if (totalLines <= maxLines) {
    return [html ? sanitizeRichTextHtml(raw, PRINT_SANITIZE) : raw];
  }

  const chunks: string[] = [];
  let buf: string[] = [];
  let lines = 0;

  const joinBuf = () => (html ? buf.join('') : buf.join('\n\n'));
  const flush = () => {
    if (!buf.length) return;
    chunks.push(joinBuf());
    buf = [];
    lines = 0;
  };

  for (const unit of units) {
    const unitLines = Math.max(1, estimateDensePrintLines(richTextToPlain(unit), cpl));

    if (unitLines > maxLines && lines === 0) {
      const plainLines = richTextToPlain(unit)
        .split('\n')
        .map((l) => l.trimEnd())
        .filter((l) => l.trim());
      let slice: string[] = [];
      let sliceLines = 0;
      for (const pl of plainLines) {
        const plLines = Math.max(1, estimateDensePrintLines(pl, cpl));
        if (sliceLines > 0 && sliceLines + plLines > maxLines) {
          chunks.push(html ? escapePlainTextAsHtml(slice.join('\n')) : slice.join('\n'));
          slice = [];
          sliceLines = 0;
        }
        slice.push(pl);
        sliceLines += plLines;
      }
      if (slice.length) {
        chunks.push(html ? escapePlainTextAsHtml(slice.join('\n')) : slice.join('\n'));
      }
      continue;
    }

    if (lines > 0 && lines + unitLines > maxLines) {
      flush();
    }
    buf.push(unit);
    lines += unitLines;
  }
  flush();

  return chunks.filter((c) => richTextToPlain(c).trim());
}

/** Legacy helper for callers that want description-style splits of plain text only. */
export function buildPlainDescriptionChunks(value: string): string[] {
  return splitDescriptionChunks(value).filter(Boolean);
}
